import { useState } from "react"
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { CLASSIFICATION_OPTIONS, type SummarizerLabel } from "@/utils/summarizer-config"

import type { DocumentClassificationType } from "@/utils/types"

const UNSET = "__unset__"

interface ClassificationsPanelProps {
  label: SummarizerLabel
  classifications: DocumentClassificationType[]
  classificationsFailed?: boolean
  canWrite: boolean
  onChange: (updater: (label: SummarizerLabel) => SummarizerLabel) => void
}

export function ClassificationsPanel({
  label,
  classifications,
  classificationsFailed = false,
  canWrite,
  onChange,
}: ClassificationsPanelProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set())
  const [selectedRows, setSelectedRows] = useState<Set<string>>(() => new Set())
  const [bulkValue, setBulkValue] = useState("")

  function setGroup(group: DocumentClassificationType, value: string | undefined) {
    onChange((current) => {
      const documentClasses = { ...current.documentClasses }
      const documentSubClasses = { ...current.documentSubClasses }
      const subClasses = { ...(documentSubClasses[group.name] ?? {}) }
      if (value == null) delete documentClasses[group.name]
      else documentClasses[group.name] = value

      for (const subClass of group.subClassifications ?? []) {
        if (value == null) delete subClasses[subClass.name]
        else subClasses[subClass.name] = value
      }

      documentSubClasses[group.name] = subClasses

      return { ...current, documentClasses, documentSubClasses }
    })
  }

  function setSubClass(group: string, subClass: string, value: string | undefined) {
    onChange((current) => {
      const subs = { ...(current.documentSubClasses[group] ?? {}) }
      if (value == null) delete subs[subClass]
      else subs[subClass] = value

      return { ...current, documentSubClasses: { ...current.documentSubClasses, [group]: subs } }
    })
  }

  function toggleExpanded(groupName: string) {
    setExpandedGroups((current) => {
      const next = new Set(current)
      if (next.has(groupName)) next.delete(groupName)
      else next.add(groupName)
      return next
    })
  }

  function toggleGroup(group: DocumentClassificationType, checked: boolean) {
    const groupKey = JSON.stringify(["group", group.name])
    const subClassKeys = (group.subClassifications ?? []).map((subClass) =>
      JSON.stringify(["subClass", group.name, subClass.name]),
    )

    setSelectedRows((current) => {
      const next = new Set(current)
      if (checked) {
        next.add(groupKey)
        subClassKeys.forEach((key) => next.add(key))
      } else {
        next.delete(groupKey)
        subClassKeys.forEach((key) => next.delete(key))
      }
      return next
    })
  }

  function toggleSubClass(groupName: string, subClassName: string, checked: boolean) {
    const groupKey = JSON.stringify(["group", groupName])
    const rowKey = JSON.stringify(["subClass", groupName, subClassName])

    setSelectedRows((current) => {
      const next = new Set(current)
      next.delete(groupKey)
      if (checked) next.add(rowKey)
      else next.delete(rowKey)
      return next
    })
  }

  function applyBulkValue() {
    if (!canWrite || selectedRows.size === 0 || bulkValue === "") return
    const value = bulkValue === UNSET ? undefined : bulkValue

    onChange((current) => {
      const documentClasses = { ...current.documentClasses }
      const documentSubClasses = Object.fromEntries(
        Object.entries(current.documentSubClasses).map(([group, subClasses]) => [group, { ...subClasses }]),
      )

      for (const group of classifications) {
        const groupKey = JSON.stringify(["group", group.name])
        if (selectedRows.has(groupKey)) {
          if (value == null) delete documentClasses[group.name]
          else documentClasses[group.name] = value
        }

        for (const subClass of group.subClassifications ?? []) {
          const subClassKey = JSON.stringify(["subClass", group.name, subClass.name])
          if (!selectedRows.has(subClassKey)) continue
          const values = { ...(documentSubClasses[group.name] ?? {}) }
          if (value == null) delete values[subClass.name]
          else values[subClass.name] = value
          documentSubClasses[group.name] = values
        }
      }

      return { ...current, documentClasses, documentSubClasses }
    })

    setSelectedRows(new Set())
    setBulkValue("")
  }

  if (classifications.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {classificationsFailed
          ? "Document classifications could not be loaded."
          : "This brand has no document classifications to configure."}
      </p>
    )
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto rounded-md border">
        <div className="divide-y">
          {classifications.map((group) => {
            const subClassifications = group.subClassifications ?? []
            const isExpanded = expandedGroups.has(group.name)
            const groupValue = label.documentClasses[group.name] ?? "Not set"
            const groupKey = JSON.stringify(["group", group.name])
            const subClassKeys = subClassifications.map((subClass) =>
              JSON.stringify(["subClass", group.name, subClass.name]),
            )
            const selectedSubClassCount = subClassKeys.filter((key) => selectedRows.has(key)).length
            const groupSelection =
              selectedRows.has(groupKey) || (subClassKeys.length > 0 && selectedSubClassCount === subClassKeys.length)
                ? true
                : selectedSubClassCount > 0
                  ? "indeterminate"
                  : false

            return (
              <div key={group.name}>
                <div className="grid min-h-12 grid-cols-[1.25rem_1.5rem_minmax(0,1fr)] items-center gap-2 px-3 py-3 lg:grid-cols-[1.25rem_1.5rem_minmax(0,1fr)_auto_9rem] lg:py-0">
                  <Checkbox
                    className="cursor-pointer disabled:cursor-not-allowed"
                    aria-label={`Select ${group.name}`}
                    checked={groupSelection}
                    disabled={!canWrite}
                    onCheckedChange={(checked) => toggleGroup(group, checked === true)}
                  />
                  {subClassifications.length > 0 ? (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-6 cursor-pointer"
                      aria-label={`${isExpanded ? "Collapse" : "Expand"} ${group.name}`}
                      onClick={() => toggleExpanded(group.name)}
                    >
                      {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                    </Button>
                  ) : (
                    <span className="size-6" />
                  )}
                  <span className="min-w-0 text-sm font-semibold max-lg:[overflow-wrap:anywhere] lg:truncate">
                    {group.name}
                  </span>
                  <span className="col-span-3 text-xs text-muted-foreground max-lg:[overflow-wrap:anywhere] lg:col-span-1 lg:whitespace-nowrap">
                    {subClassifications.length} {groupValue}
                  </span>
                  <SettingSelect
                    value={label.documentClasses[group.name]}
                    ariaLabel={`Setting for ${group.name}`}
                    disabled={!canWrite}
                    onChange={(value) => setGroup(group, value)}
                  />
                </div>

                {isExpanded &&
                  subClassifications.map((subClass) => {
                    const rowKey = JSON.stringify(["subClass", group.name, subClass.name])
                    const subClassValue = label.documentSubClasses[group.name]?.[subClass.name] ?? "Not set"

                    return (
                      <div
                        key={rowKey}
                        className="grid min-h-11 grid-cols-[1.25rem_1.5rem_minmax(0,1fr)] items-center gap-2 border-t px-3 py-3 pl-5 lg:grid-cols-[1.25rem_1.5rem_minmax(0,1fr)_auto_9rem] lg:py-0 lg:pl-8"
                      >
                        <Checkbox
                          className="cursor-pointer disabled:cursor-not-allowed"
                          aria-label={`Select ${group.name} / ${subClass.name}`}
                          checked={selectedRows.has(rowKey)}
                          disabled={!canWrite}
                          onCheckedChange={(checked) => toggleSubClass(group.name, subClass.name, checked === true)}
                        />
                        <span className="size-6" />
                        <span className="min-w-0 text-sm max-lg:[overflow-wrap:anywhere] lg:truncate">
                          {subClass.name}
                        </span>
                        <span className="col-span-3 text-xs text-muted-foreground max-lg:[overflow-wrap:anywhere] lg:col-span-1 lg:whitespace-nowrap">
                          {subClassValue}
                        </span>
                        <SettingSelect
                          value={label.documentSubClasses[group.name]?.[subClass.name]}
                          ariaLabel={`Setting for ${group.name} / ${subClass.name}`}
                          disabled={!canWrite}
                          onChange={(value) => setSubClass(group.name, subClass.name, value)}
                        />
                      </div>
                    )
                  })}
              </div>
            )
          })}
        </div>
      </div>

      {canWrite && (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-t bg-background py-3">
          <span className="mr-auto text-sm font-medium text-muted-foreground max-lg:w-full">
            {selectedRows.size === 0
              ? "Select classifications to update them in bulk"
              : `${selectedRows.size} selected`}
          </span>
          <label htmlFor="bulk-classification-setting" className="cursor-pointer text-sm">
            Set to
          </label>
          <Select value={bulkValue} onValueChange={setBulkValue}>
            <SelectTrigger
              id="bulk-classification-setting"
              className="w-36 cursor-pointer max-lg:min-w-0 max-lg:flex-1"
              aria-label="Bulk classification setting"
            >
              <SelectValue placeholder="Choose setting" />
            </SelectTrigger>
            <SelectContent className="max-lg:max-w-(--radix-select-content-available-width) max-lg:[&_[data-slot=select-item]]:[overflow-wrap:anywhere]">
              <SelectItem value={UNSET} className="cursor-pointer">
                Not set
              </SelectItem>
              {CLASSIFICATION_OPTIONS.map((option) => (
                <SelectItem key={option} value={option} className="cursor-pointer">
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedRows.size > 0 && (
            <Button type="button" variant="ghost" className="cursor-pointer" onClick={() => setSelectedRows(new Set())}>
              Clear selection
            </Button>
          )}
          <Button
            type="button"
            className="cursor-pointer disabled:pointer-events-auto disabled:cursor-not-allowed"
            disabled={selectedRows.size === 0 || bulkValue === ""}
            onClick={applyBulkValue}
          >
            Apply
          </Button>
        </div>
      )}
    </div>
  )
}

function SettingSelect({
  value,
  ariaLabel,
  disabled,
  onChange,
}: {
  value: string | undefined
  ariaLabel: string
  disabled: boolean
  onChange: (value: string | undefined) => void
}) {
  const isKnown = value == null || (CLASSIFICATION_OPTIONS as readonly string[]).includes(value)

  return (
    <Select
      value={value ?? UNSET}
      onValueChange={(next) => onChange(next === UNSET ? undefined : next)}
      disabled={disabled}
    >
      <SelectTrigger
        className="col-span-3 w-full min-w-0 cursor-pointer lg:col-span-1 lg:w-36 max-lg:[&>[data-slot=select-value]]:min-w-0"
        aria-label={ariaLabel}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-lg:max-w-(--radix-select-content-available-width) max-lg:[&_[data-slot=select-item]]:[overflow-wrap:anywhere]">
        <SelectItem value={UNSET} className="cursor-pointer">
          Not set
        </SelectItem>
        {CLASSIFICATION_OPTIONS.map((option) => (
          <SelectItem key={option} value={option} className="cursor-pointer">
            {option}
          </SelectItem>
        ))}
        {!isKnown && value != null && (
          <SelectItem value={value} className="cursor-pointer">
            {value}
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  )
}
